import { useCallback, useState } from 'react';

/**
 * Tiles or list, remembered between visits.
 *
 * The preference is per person and per browser, in localStorage - it is not a
 * column on app_user. Which of two layouts somebody likes is not project data
 * and does not belong in the database or in a round trip to the API.
 *
 * Every read and write is guarded. localStorage throws on access in a private
 * window and is absent altogether under the plain-function tests, and a view
 * toggle is not worth taking the page down over: on any failure the caller
 * gets the default and the choice simply does not stick.
 */
export const VIEW_TILES = 'tiles';
export const VIEW_LIST = 'list';

const STORAGE_KEY = 'westwood.projectViewMode';

function readStored() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === VIEW_LIST || stored === VIEW_TILES ? stored : null;
  } catch {
    return null;
  }
}

export function useProjectViewMode(defaultMode = VIEW_TILES) {
  const [mode, setMode] = useState(() => readStored() || defaultMode);

  const setViewMode = useCallback(next => {
    setMode(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* The choice applies to this page; it just will not be remembered. */
    }
  }, []);

  return [mode, setViewMode];
}
